import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { dashboardPathFor } from '../auth/dashboardPath.ts';
import { Logo } from '../components/Logo.tsx';

export function ActivatePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { activate } = useAuth();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<{ email: string; firstName: string } | 'invalid' | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setInvitation('invalid');
      return;
    }
    api
      .get<{ email: string; firstName: string }>(`/auth/invitation/${encodeURIComponent(token)}`)
      .then(setInvitation)
      .catch(() => setInvitation('invalid'));
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setLoading(true);
    try {
      const user = await activate(token, password);
      navigate(dashboardPathFor(user.role));
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'WEAK_PASSWORD' ? 'Le mot de passe doit faire au moins 8 caractères.' : 'Ce lien n’est plus valide.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="app-shell">
      <div className="screen-content" style={{ justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <Logo size={40} withText={false} />
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--accent-dark)' }}>Activer mon compte</div>
        </div>

        {invitation === null && <div className="empty-state">Vérification du lien…</div>}

        {invitation === 'invalid' && (
          <div className="error-banner">
            Ce lien d'invitation est invalide ou a expiré. Demandez-en un nouveau à l'équipe Mon Extra.
          </div>
        )}

        {invitation && invitation !== 'invalid' && (
          <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', textAlign: 'center' }}>
              Bonjour {invitation.firstName}, choisissez le mot de passe de votre compte <strong>{invitation.email}</strong>.
            </div>
            {error && <div className="error-banner">{error}</div>}
            <div>
              <label className="field-label" htmlFor="password">Mot de passe</label>
              <input id="password" className="input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
            </div>
            <div>
              <label className="field-label" htmlFor="confirm">Confirmer le mot de passe</label>
              <input id="confirm" className="input" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Activation…' : 'Activer mon compte'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
